import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { RemoveSiteRequestModel, SiteUpsearchModel, SiteModel, SiteSharedPhoneModel } from '@trialcard/apigateway.models';
import { ApigatewayApi } from '@trialcard/apigateway.client';
import * as enums from '@trialcard/enums';
import { orderBy } from 'lodash';
import SessionModule from './session';
import { OnClear } from '~/services/clearModule';
import { Settings } from '~/services/settings';
import CommonTools from '~/components/tools/commonTools';


/* eslint-disable dot-notation */
@InjectModule({ stateFactory: true }, module)
@OnClear<PracticeModule>(async value => value.clear())
export default class PracticeModule extends InjectVuexModule {
	@Resolve
	apiGateway!: ApigatewayApi;
	@Resolve
	public settings!: Settings;
	@Resolve
	session!: SessionModule;

	practiceModels: SiteModel[] = [];
	patientSearchPractice: SiteModel[] = [];
	mainSiteId: number | null = null;

	@Mutation
	clear() { }

	@Mutation
	setPracticeModel(practices: SiteModel[]) {
		this.practiceModels = practices;
	}

	@Mutation
	setMainSiteId(siteId: number) {
		this.mainSiteId = siteId;
	}

	@Mutation
	setSearchPracticeModel(practices: SiteModel[]) {
		this.patientSearchPractice = practices;
	}

	private mapSiteData(sites: SiteModel[]) {
		return (sites || []).map(item => {
			item['fax'] = this.getCurrentFaxNumber(item.phoneNumbers) || '';
			item['phone'] = this.getCurrentPhoneNumber(item.phoneNumbers) || '';
			item['address'] = `${(item.addresses[0] || {}).addressOne}` || '';
			item['addressTwo'] = `${(item.addresses[0] || {}).addressTwo}` || '';
			item['city'] = (item.addresses[0] || {}).city || '';
			item['zip'] = (item.addresses[0] || {}).zip || '';
			item['state'] = (item.addresses[0] || {}).state || '';
			item['disabled'] = false;
		})
	}

	@Action({ rawError: true })
	async getPracticesAPI(body = {} as any) {
		try {
			let results;
			if (this.session.isHub) {
				results = await this.apiGateway.edgeAccount
					.sitesHubGetSites(body, this.settings.programId)
					.then(data => {
						this.mapSiteData(data.data.data)
						return data;
					})
					.catch(error => {
						throw error;
					});
			} else {
				results = await this.apiGateway.edgeAccount
					.accountGetSites(body, this.settings.programId)
					.then(data => {
						this.mapSiteData(data.data.data || [])
						return data;
					})
					.catch(error => {
						throw error;
					});
			}
			if (body.siteId) {
				this.setSearchPracticeModel(results.data.data || [])
			} else {
				this.setPracticeModel(results.data.data || []);
				await this.getSiteAccountSiteId();
			}
			return this.practiceModels;
		} catch {
			return;
		}
	}
	@Action({ rawError: true })
	async addSiteAPI(site: SiteUpsearchModel) {
		try {
			const saveCall = await this.apiGateway.edgeAccount.accountSaveSite(site, this.settings.programId);

			return (saveCall.data || {}).success;
		} catch {
			return;
		}
	}
	@Action({ rawError: true })
	async deleteSiteAPI(siteId: number) {
		try {
			const site = { siteId } as RemoveSiteRequestModel;
			const postDeleteCall = await this.apiGateway.edgeAccount.accountRemoveSite(site, this.settings.programId);

			return (postDeleteCall.data || {}).success;
		} catch {
			return;
		}
	}
	@Action({})
	async getSiteAccountSiteId() {
		try {
			const siteAccountResponse = await this.apiGateway.edgeSiteAccount.siteAccountGetSiteAccount({});
			const siteAccount = siteAccountResponse
				&& siteAccountResponse.data
				&& siteAccountResponse.data.data
			const partyId = siteAccount && siteAccount.partyId;
			if (partyId) this.setMainSiteId(partyId);
			return;
		} catch {
			return;
		}
	}

	private getCurrentPhoneNumber(data: SiteSharedPhoneModel[]) {
		const phoneNumbers = data.filter(x => x.phoneType === enums.ContactInfoType.PHONE);
		if (phoneNumbers.length === 0) return;
		const currentPhoneNumberModel = orderBy(phoneNumbers, ph => ph.phoneId, 'desc')[0];
		return CommonTools.stringToPhone(currentPhoneNumberModel.phoneNumber!);
	}

	private getCurrentFaxNumber(data: SiteSharedPhoneModel[]) {
		const phoneNumbers = data.filter(x => x.phoneType === enums.ContactInfoType.FAX);
		if (phoneNumbers.length === 0) return;
		const currentPhoneNumberModel = orderBy(phoneNumbers, ph => ph.phoneId, 'desc')[0];
		return CommonTools.stringToPhone(currentPhoneNumberModel.phoneNumber!);
	}
}
